import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Button,
  ButtonGroup,
} from '@mui/material';
import {
  Map as MapIcon,
  TrendingUp as TrendIcon,
  Compare as CompareIcon,
  Psychology as AIIcon,
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  LocationOn as LocationIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  AccountBalance as RegionIcon,
  BarChart as ChartIcon,
  List as PriorityIcon,
  Assessment as ReportsIcon,
  TableView as ExcelIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getAnalysisData, getAvailableYears, getChartData, getRegions, getDistricts, type AnalysisData, type GeographicData } from '../../../services/analysisApi';
import { getEnrollmentOverview, getSchoolEnrollmentDetails, getEnrollmentChartData, exportEnrollmentExcel, type EnrollmentOverview, type SchoolEnrollmentDetails } from '../../../services/enrollmentApi';
import { syncEnrollmentData, triggerFullSync, getEnrollmentSyncStatus, type SyncResult, type SyncStatus } from '../../../services/enrollmentSyncApi';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analysis-tabpanel-${index}`}
      aria-labelledby={`analysis-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ANALYSIS_LEVELS = [
  { value: 'region', label: 'Regional View', icon: <RegionIcon /> },
  { value: 'district', label: 'District View', icon: <LocationIcon /> },
  { value: 'school', label: 'School View', icon: <SchoolIcon /> },
];

const ANALYSIS_TYPES = [
  { value: 'enrollment', label: 'Student Enrollment', color: 'primary' },
  { value: 'infrastructure', label: 'School Infrastructure', color: 'secondary' },
  { value: 'performance', label: 'Academic Performance', color: 'success' },
  { value: 'staff', label: 'Staff Distribution', color: 'info' },
  { value: 'gender_parity', label: 'Gender Parity Index', color: 'warning' },
];

const CHART_STYLES = [
  { value: 'bar', label: 'Bar graphs' },
  { value: 'line', label: 'Line charts' },
  { value: 'pie', label: 'Pie charts' },
  { value: 'area', label: 'Area charts' },
];

const SORT_OPTIONS = [
  { value: 'none', label: '(no sort)' },
  { value: 'asc', label: 'Ascending' },
  { value: 'desc', label: 'Descending' },
  { value: 'alphabetical', label: 'Alphabetical' },
];

const SPLIT_BY_OPTIONS = [
  { value: 'none', label: '(none)' },
  { value: 'region', label: 'By Region' },
  { value: 'district', label: 'By District' },
  { value: 'school_level', label: 'By School Level' },
  { value: 'gender', label: 'By Gender' },
];

const AdvancedAnalysis: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedLevel, setSelectedLevel] = useState('school');
  const [selectedType, setSelectedType] = useState('enrollment');
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('2026');
  const [mapView, setMapView] = useState('standard'); // standard, satellite, terrain
  const [isExporting, setIsExporting] = useState(false);
  const [exportingType, setExportingType] = useState<string>('');
  const [exportedFile, setExportedFile] = useState<{blob: Blob, filename: string} | null>(null);
  
  // Chart configuration state (similar to Java EMIS interface)
  const [chartConfig, setChartConfig] = useState({
    indicator: 'enrollment',
    xAxis: 'regions',
    splitBy: 'none',
    year: '2026',
    chartStyle: 'bar',
    sort: 'none'
  });
  
  // Filter states for exports
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [selectedSchoolType, setSelectedSchoolType] = useState<string>('');

  // Sync state
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);

  // Fetch enrollment data from Django models
  const { data: enrollmentData, isLoading: enrollmentLoading, error: enrollmentError } = useQuery({
    queryKey: ['enrollment-overview', selectedAcademicYear],
    queryFn: () => getEnrollmentOverview(selectedAcademicYear ? parseInt(selectedAcademicYear) : undefined),
    enabled: tabValue === 5, // Only fetch when enrollment tab is active
  });

  const { data: schoolEnrollmentData, isLoading: schoolEnrollmentLoading } = useQuery({
    queryKey: ['school-enrollment', selectedAcademicYear],
    queryFn: () => getSchoolEnrollmentDetails(undefined, parseInt(selectedAcademicYear)),
    enabled: tabValue === 5, // Only fetch when enrollment tab is active
  });

  // Fetch sync status
  const { data: syncStatus, refetch: refetchSyncStatus } = useQuery({
    queryKey: ['enrollment-sync-status', selectedAcademicYear],
    queryFn: () => getEnrollmentSyncStatus(selectedAcademicYear ? parseInt(selectedAcademicYear) : undefined),
    enabled: tabValue === 5, // Only fetch when enrollment tab is active
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Handle enrollment sync
  const handleEnrollmentSync = async (clearExisting: boolean = false) => {
    try {
      setIsSyncing(true);
      setSyncResult(null);

      const result = await syncEnrollmentData(
        selectedAcademicYear ? parseInt(selectedAcademicYear) : undefined,
        clearExisting
      );

      setSyncResult(result);
      
      // Refetch enrollment data and sync status
      setTimeout(() => {
        refetchSyncStatus();
        // Trigger a refetch of enrollment data
        window.location.reload(); // Simple approach - could be improved with query invalidation
      }, 1000);

    } catch (error) {
      setSyncResult({
        status: 'error',
        message: (error as Error)?.message || 'Sync failed'
      });
    } finally {
      setIsSyncing(false);
    }
  };

  // Handle full SIS sync
  const handleFullSync = async () => {
    try {
      setIsSyncing(true);
      setSyncResult(null);

      const result = await triggerFullSync();
      setSyncResult(result);
      
      // Refetch data
      setTimeout(() => {
        refetchSyncStatus();
        window.location.reload();
      }, 2000);

    } catch (error) {
      setSyncResult({
        status: 'error',
        message: (error as Error)?.message || 'Full sync failed'
      });
    } finally {
      setIsSyncing(false);
    }
  };

  // Fetch geographic analysis data
  const { data: geoData, isLoading: geoLoading, error: geoError } = useQuery({
    queryKey: ['geographic-analysis', selectedLevel, selectedType, selectedAcademicYear],
    queryFn: () => getAnalysisData('geographic', {
      level: selectedLevel,
      type: selectedType,
      academic_year: selectedAcademicYear || undefined
    }),
    enabled: tabValue !== 5, // Disable when enrollment tab is active to avoid conflicts
  });


  // Fetch trend analysis
  const { data: trendData, isLoading: trendLoading } = useQuery({
    queryKey: ['trend-analysis', selectedType],
    queryFn: () => getAnalysisData('trends', {
      dimension: selectedType,
      period: '5years'
    }),
  });

  // Fetch performance indicators
  const { data: kpiData, isLoading: kpiLoading } = useQuery({
    queryKey: ['performance-indicators'],
    queryFn: () => getAnalysisData('kpis', { scope: 'national' }),
  });

  // Fetch available years
  const { data: availableYears } = useQuery({
    queryKey: ['available-years'],
    queryFn: getAvailableYears,
  });

  // Fetch regions for filtering
  const { data: regions } = useQuery({
    queryKey: ['regions'],
    queryFn: getRegions,
  });

  // Fetch districts for filtering
  const { data: districts } = useQuery({
    queryKey: ['districts', selectedRegion],
    queryFn: () => getDistricts(selectedRegion || undefined),
    enabled: !!selectedRegion,
  });

  // Fetch chart data
  const { data: chartData, isLoading: chartLoading } = useQuery({
    queryKey: ['chart-data', chartConfig.indicator, chartConfig.xAxis, chartConfig.splitBy, chartConfig.year],
    queryFn: () => getChartData({
      indicator: chartConfig.indicator,
      xAxis: chartConfig.xAxis,
      splitBy: chartConfig.splitBy,
      year: chartConfig.year
    }),
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getAnalysisColor = (type: string) => {
    const typeConfig = ANALYSIS_TYPES.find(t => t.value === type);
    return typeConfig?.color || 'default';
  };

  const handleExportSchoolsExcel = async (reportType: string = 'schools') => {
    console.log(`Export button clicked for report type: ${reportType}`);
    if (isExporting) {
      console.log('Export already in progress, skipping...');
      return; // Prevent multiple simultaneous exports
    }
    
    setIsExporting(true);
    setExportingType(reportType);
    setExportedFile(null); // Clear any previous exported file
    try {
      console.log(`Starting ${reportType} export for year ${chartConfig.year}...`);
      console.log('Filters:', { region: selectedRegion, district: selectedDistrict, schoolType: selectedSchoolType });
      
      const params = new URLSearchParams();
      if (chartConfig.year) params.append('year', chartConfig.year);
      params.append('report_type', reportType);
      
      // Add region and district filters if selected
      if (selectedRegion) params.append('region_id', selectedRegion);
      if (selectedDistrict) params.append('district_id', selectedDistrict);
      if (selectedSchoolType) params.append('school_type', selectedSchoolType);
      
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log('Export timeout reached, aborting...');
        controller.abort();
      }, 120000); // 120 second timeout for large exports
      
      const url = `http://localhost:8000/api/v1/sis-integration/export-schools-excel/?${params.toString()}`;
      console.log('Fetching from:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      console.log('Response received:', response.status, response.statusText);
      console.log('Response headers:', response.headers);

      if (!response.ok) {
        let errorMessage = `${response.status} ${response.statusText}`;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.detail || errorMessage;
          } else {
            const errorText = await response.text();
            if (errorText) {
              errorMessage = errorText;
            }
          }
        } catch (e) {
          // If we can't parse the error, use the default message
        }
        console.error('Export failed:', response.status, errorMessage);
        throw new Error(`Failed to export Excel file: ${errorMessage}`);
      }

      console.log('Export successful, converting to blob...');
      const blob = await response.blob();
      console.log('Blob created, size:', blob.size, 'bytes');
      const filename = `Zanzibar_${reportType.charAt(0).toUpperCase() + reportType.slice(1)}_${chartConfig.year}.xlsx`;
      setExportedFile({ blob, filename });
      console.log('File ready for download!');
    } catch (error) {
      console.error('Error exporting Excel file:', error);
      let errorMessage = 'Unknown error occurred';
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorMessage = 'Export timed out. The enrollment report is large and may take longer to generate. Please try again.';
        } else {
          errorMessage = error.message;
        }
      }
      alert(`Export failed: ${errorMessage}`);
    } finally {
      setIsExporting(false);
      setExportingType('');
    }
  };

  const handleDownloadFile = () => {
    if (!exportedFile) return;
    
    const url = window.URL.createObjectURL(exportedFile.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = exportedFile.filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    setExportedFile(null); // Clear the exported file after download
    console.log('File downloaded successfully!');
  };

  const formatNumber = (value: number) => {
    return value.toLocaleString();
  };

  const renderMapVisualization = () => {
    if (geoLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (geoError) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          Error loading geographic data: {geoError instanceof Error ? geoError.message : 'Unknown error'}
        </Alert>
      );
    }

    const data = geoData?.data || [];

    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Geographic Analysis - {selectedLevel.charAt(0).toUpperCase() + selectedLevel.slice(1)} Level
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  startIcon={isExporting && exportingType === 'map' ? <CircularProgress size={16} color="inherit" /> : <ExcelIcon />}
                  onClick={() => handleExportSchoolsExcel('infrastructure')}
                  disabled={isExporting}
                >
                  Export Data
                </Button>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Level</InputLabel>
                    <Select
                      value={selectedLevel}
                      label="Level"
                      onChange={(e) => setSelectedLevel(e.target.value)}
                    >
                      {ANALYSIS_LEVELS.map((level) => (
                        <MenuItem key={level.value} value={level.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {level.icon}
                            {level.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Analysis Type</InputLabel>
                    <Select
                      value={selectedType}
                      label="Analysis Type"
                      onChange={(e) => setSelectedType(e.target.value)}
                    >
                      {ANALYSIS_TYPES.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <ButtonGroup size="small">
                    <Button
                      variant={mapView === 'standard' ? 'contained' : 'outlined'}
                      onClick={() => setMapView('standard')}
                    >
                      Standard
                    </Button>
                    <Button
                      variant={mapView === 'satellite' ? 'contained' : 'outlined'}
                      onClick={() => setMapView('satellite')}
                    >
                      Satellite
                    </Button>
                    <Button
                      variant={mapView === 'terrain' ? 'contained' : 'outlined'}
                      onClick={() => setMapView('terrain')}
                    >
                      Terrain
                    </Button>
                  </ButtonGroup>
                </Box>
              </Box>

              {/* Map Placeholder - In production, integrate with Leaflet, Google Maps, or similar */}
              <Paper 
                elevation={2} 
                sx={{ 
                  height: 400, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: '#f5f5f5',
                  backgroundImage: 'radial-gradient(circle, #e0e0e0 1px, transparent 1px)',
                  backgroundSize: '20px 20px'
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <MapIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Interactive Map View
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedLevel.charAt(0).toUpperCase() + selectedLevel.slice(1)} level {selectedType} analysis
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Showing {data.length} {selectedLevel}s • View: {mapView}
                  </Typography>
                </Box>
              </Paper>

              {/* Data Summary */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Analysis Summary
                </Typography>
                <Grid container spacing={2}>
                  {data.slice(0, 4).map((item: any, index: number) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <Card variant="outlined">
                        <CardContent sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {item.region_name || item.district_name || item.school_name}
                          </Typography>
                          <Typography variant="h6">
                            {formatNumber(item.total_students || 0)}
                          </Typography>
                          <Typography variant="caption">
                            Students
                          </Typography>
                          {item.utilization_rate && (
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(item.utilization_rate, 100)} 
                              sx={{ mt: 1 }}
                            />
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderTrendAnalysis = () => {
    if (trendLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    const trends = trendData?.data?.trends || [];

    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Trend Analysis - {selectedType.charAt(0).toUpperCase() + selectedType.slice(1)}
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<ExcelIcon />}
                  onClick={() => handleExportSchoolsExcel('enrollment')}
                  disabled={isExporting}
                >
                  Export Trend Data
                </Button>
              </Box>
              
              {/* Trend Chart Placeholder */}
              <Paper 
                elevation={1} 
                sx={{ 
                  height: 300, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mb: 3,
                  backgroundColor: '#fafafa'
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <TrendIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6">Trend Visualization</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {trends.length} years of data
                  </Typography>
                </Box>
              </Paper>

              {/* Trend Data Table */}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Academic Year</strong></TableCell>
                      <TableCell align="right"><strong>Total Students</strong></TableCell>
                      <TableCell align="right"><strong>Male</strong></TableCell>
                      <TableCell align="right"><strong>Female</strong></TableCell>
                      <TableCell align="right"><strong>Growth Rate</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trends.map((trend: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>{trend.academic_year}</TableCell>
                        <TableCell align="right">{formatNumber(trend.total_students)}</TableCell>
                        <TableCell align="right">{formatNumber(trend.male_students)}</TableCell>
                        <TableCell align="right">{formatNumber(trend.female_students)}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${trend.growth_rate || 0}%`}
                            color={trend.growth_rate > 0 ? 'success' : trend.growth_rate < 0 ? 'error' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderPerformanceIndicators = () => {
    if (kpiLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    const kpis = kpiData?.data || {};

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <SchoolIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="primary">
                  Total Schools
                </Typography>
              </Box>
              <Typography variant="h4">
                {formatNumber(kpis.total_schools || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PeopleIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h6" color="info.main">
                  Total Students
                </Typography>
              </Box>
              <Typography variant="h4">
                {formatNumber(kpis.total_students || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main" gutterBottom>
                Student-Teacher Ratio
              </Typography>
              <Typography variant="h4">
                {kpis.student_teacher_ratio || 0}:1
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="secondary.main" gutterBottom>
                Female Enrollment
              </Typography>
              <Typography variant="h4">
                {kpis.female_enrollment_pct || 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Gender Parity & Efficiency Indicators
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Gender Parity Index
                    </Typography>
                    <Typography variant="h5">
                      {kpis.gender_parity_index || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Target: 1.0 (perfect parity)
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Schools per 1000 Students
                    </Typography>
                    <Typography variant="h5">
                      {kpis.schools_per_1000_students || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Efficiency indicator
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderChartConfiguration = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Chart Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure your chart view. Select dimensions, filters, and styles.
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Indicator</InputLabel>
              <Select
                value={chartConfig.indicator}
                label="Indicator"
                onChange={(e) => setChartConfig(prev => ({ ...prev, indicator: e.target.value }))}
              >
                {ANALYSIS_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>X-Axis</InputLabel>
              <Select
                value={chartConfig.xAxis}
                label="X-Axis"
                onChange={(e) => setChartConfig(prev => ({ ...prev, xAxis: e.target.value }))}
              >
                <MenuItem value="all">All Nations</MenuItem>
                <MenuItem value="regions">By Regions</MenuItem>
                <MenuItem value="districts">By Districts</MenuItem>
                <MenuItem value="schools">By Schools</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Split by</InputLabel>
              <Select
                value={chartConfig.splitBy}
                label="Split by"
                onChange={(e) => setChartConfig(prev => ({ ...prev, splitBy: e.target.value }))}
              >
                {SPLIT_BY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Year</InputLabel>
              <Select
                value={chartConfig.year}
                label="Year"
                onChange={(e) => setChartConfig(prev => ({ ...prev, year: e.target.value }))}
              >
                {availableYears?.map((year) => (
                  <MenuItem key={year} value={year.toString()}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Chart Style</InputLabel>
              <Select
                value={chartConfig.chartStyle}
                label="Chart Style"
                onChange={(e) => setChartConfig(prev => ({ ...prev, chartStyle: e.target.value }))}
              >
                {CHART_STYLES.map((style) => (
                  <MenuItem key={style.value} value={style.value}>
                    {style.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Sort</InputLabel>
              <Select
                value={chartConfig.sort}
                label="Sort"
                onChange={(e) => setChartConfig(prev => ({ ...prev, sort: e.target.value }))}
              >
                {SORT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <Button 
              variant="contained" 
              color="primary"
              startIcon={<ChartIcon />}
              onClick={() => {
                // Force refresh of chart data by updating React Query cache
                window.location.hash = `#chart-${Date.now()}`;
              }}
              sx={{ 
                backgroundColor: '#333',
                '&:hover': { backgroundColor: '#555' },
                borderRadius: 1,
                textTransform: 'none',
                px: 3,
                py: 1
              }}
            >
              View {chartConfig.chartStyle.charAt(0).toUpperCase() + chartConfig.chartStyle.slice(1)} Chart
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Advanced Analysis Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Comprehensive data analysis with charts, maps, reports, and insights - Java EMIS style interface
        </Typography>
      </Box>


      {/* Main Navigation Tabs - Similar to Java EMIS */}
      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="analysis tabs">
          <Tab 
            label="Charts" 
            icon={<ChartIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Maps" 
            icon={<MapIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Priority List" 
            icon={<PriorityIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Reports" 
            icon={<ReportsIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Excel Reports" 
            icon={<ExcelIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Enrollment Data" 
            icon={<PeopleIcon />} 
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        {/* Charts Tab - Similar to Java EMIS Chart Configuration */}
        {renderChartConfiguration()}
        
        {/* Chart Visualization Area */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                {ANALYSIS_TYPES.find(t => t.value === chartConfig.indicator)?.label} - {chartConfig.chartStyle.charAt(0).toUpperCase() + chartConfig.chartStyle.slice(1)} Chart
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={isExporting && exportingType === 'chart' ? <CircularProgress size={16} color="inherit" /> : <ExcelIcon />}
                onClick={() => handleExportSchoolsExcel('enrollment')}
                disabled={isExporting}
              >
                Export Data
              </Button>
            </Box>
            
            {chartLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Real Chart Data Display */}
                <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {chartConfig.indicator} by {chartConfig.xAxis} • Year: {chartConfig.year} • Data points: {chartData?.data?.length || 0}
                  </Typography>
                </Paper>
                
                {/* Chart Visualization */}
                {chartConfig.chartStyle === 'bar' && chartData?.data?.length > 0 && (
                  <Paper elevation={1} sx={{ p: 3, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      {chartConfig.indicator.charAt(0).toUpperCase() + chartConfig.indicator.slice(1)} Bar Chart
                    </Typography>
                    
                    {/* Simple Bar Chart using CSS */}
                    <Box sx={{ mt: 2 }}>
                      {chartData.data.slice(0, 10).map((item: any, index: number) => {
                        const maxValue = Math.max(...chartData.data.map((d: any) => d.value || 0));
                        const percentage = ((item.value || 0) / maxValue) * 100;
                        
                        return (
                          <Box key={index} sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 120 }}>
                                {item.label}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formatNumber(item.value || 0)}
                              </Typography>
                            </Box>
                            <Box 
                              sx={{ 
                                width: '100%', 
                                height: 24, 
                                backgroundColor: '#f5f5f5', 
                                borderRadius: 1,
                                overflow: 'hidden'
                              }}
                            >
                              <Box 
                                sx={{ 
                                  width: `${percentage}%`, 
                                  height: '100%', 
                                  backgroundColor: chartConfig.splitBy === 'gender' ? 
                                    'linear-gradient(90deg, #1976d2 0%, #1976d2 ' + ((item.male || 0) / (item.value || 1) * 100) + '%, #e91e63 ' + ((item.male || 0) / (item.value || 1) * 100) + '%, #e91e63 100%)' :
                                    '#1976d2',
                                  transition: 'width 0.5s ease-in-out'
                                }}
                              />
                            </Box>
                            {chartConfig.splitBy === 'gender' && (
                              <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                <Typography variant="caption" color="primary">
                                  Male: {formatNumber(item.male || 0)}
                                </Typography>
                                <Typography variant="caption" color="secondary">
                                  Female: {formatNumber(item.female || 0)}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Paper>
                )}

                {/* Pie Chart for Chart Style */}
                {chartConfig.chartStyle === 'pie' && chartData?.data?.length > 0 && (
                  <Paper elevation={1} sx={{ p: 3, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      {chartConfig.indicator.charAt(0).toUpperCase() + chartConfig.indicator.slice(1)} Distribution
                    </Typography>
                    
                    {/* Simple representation - showing percentages */}
                    <Grid container spacing={2}>
                      {chartData.data.slice(0, 8).map((item: any, index: number) => {
                        const total = chartData.data.reduce((sum: number, d: any) => sum + (d.value || 0), 0);
                        const percentage = ((item.value || 0) / total * 100).toFixed(1);
                        const colors = ['#1976d2', '#dc004e', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4'];
                        
                        return (
                          <Grid item xs={12} sm={6} md={3} key={index}>
                            <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                              <Box 
                                sx={{ 
                                  width: 60, 
                                  height: 60, 
                                  borderRadius: '50%', 
                                  backgroundColor: colors[index % colors.length],
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontWeight: 'bold',
                                  mx: 'auto',
                                  mb: 1
                                }}
                              >
                                {percentage}%
                              </Box>
                              <Typography variant="body2" noWrap>
                                {item.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatNumber(item.value || 0)}
                              </Typography>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Paper>
                )}

                {/* Line Chart placeholder */}
                {chartConfig.chartStyle === 'line' && chartData?.data?.length > 0 && (
                  <Paper elevation={1} sx={{ p: 3, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      {chartConfig.indicator.charAt(0).toUpperCase() + chartConfig.indicator.slice(1)} Trend
                    </Typography>
                    
                    <Alert severity="info">
                      Line chart visualization shows trends over time. Current data: {chartData.data.length} data points
                    </Alert>
                    
                    {/* Simple line representation */}
                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                      {chartData.data.slice(0, 6).map((item: any, index: number) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#1976d2', mr: 1 }} />
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {item.label}: {formatNumber(item.value || 0)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                )}
                
                {/* Data Table Display */}
                <TableContainer component={Paper} sx={{ maxHeight: 350 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>{chartConfig.xAxis === 'regions' ? 'Region' : chartConfig.xAxis === 'districts' ? 'District' : 'Area'}</TableCell>
                        <TableCell align="right">Total</TableCell>
                        {chartConfig.splitBy === 'gender' && (
                          <>
                            <TableCell align="right">Male</TableCell>
                            <TableCell align="right">Female</TableCell>
                            <TableCell align="right">% Female</TableCell>
                          </>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {chartData?.data?.map((item: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell component="th" scope="row">
                            {item.label}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">
                              {formatNumber(item.value || 0)}
                            </Typography>
                          </TableCell>
                          {chartConfig.splitBy === 'gender' && (
                            <>
                              <TableCell align="right">{formatNumber(item.male || 0)}</TableCell>
                              <TableCell align="right">{formatNumber(item.female || 0)}</TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={`${Math.round((item.female / (item.value || 1)) * 100)}%`}
                                  size="small"
                                  color={Math.round((item.female / (item.value || 1)) * 100) > 45 ? 'success' : 'warning'}
                                />
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Maps Tab */}
        {renderMapVisualization()}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {/* Priority List Tab */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Priority List - Performance Rankings
              </Typography>
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={isExporting && exportingType === 'priority' ? <CircularProgress size={16} color="inherit" /> : <ExcelIcon />}
                onClick={() => handleExportSchoolsExcel('schools')}
                disabled={isExporting}
              >
                Export Schools Data
              </Button>
            </Box>
            {renderPerformanceIndicators()}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        {/* Reports Tab */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Standard Reports
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={isExporting && exportingType === 'reports' ? <CircularProgress size={16} color="inherit" /> : <ExcelIcon />}
                onClick={() => handleExportSchoolsExcel('enrollment')}
                disabled={isExporting}
              >
                Export Enrollment Report
              </Button>
            </Box>
            {renderTrendAnalysis()}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        {/* Excel Reports Tab */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Schools Data Export
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  View and export schools data with enrollment and staff statistics. Use the filters below to narrow down your export to specific regions or districts.
                </Typography>
                
                {/* Export Controls */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Year</InputLabel>
                      <Select
                        value={chartConfig.year}
                        label="Year"
                        onChange={(e) => {
                          const year = e.target.value;
                          setChartConfig(prev => ({ ...prev, year }));
                          setSelectedAcademicYear(year);
                        }}
                      >
                        {availableYears?.map((year) => (
                          <MenuItem key={year} value={year.toString()}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Region (Optional)</InputLabel>
                      <Select
                        value={selectedRegion}
                        label="Region (Optional)"
                        onChange={(e) => {
                          setSelectedRegion(e.target.value);
                          setSelectedDistrict(''); // Reset district when region changes
                        }}
                      >
                        <MenuItem value="">All Regions</MenuItem>
                        {regions?.map((region) => (
                          <MenuItem key={region.region_code} value={region.region_code}>
                            {region.region_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>District (Optional)</InputLabel>
                      <Select
                        value={selectedDistrict}
                        label="District (Optional)"
                        onChange={(e) => setSelectedDistrict(e.target.value)}
                        disabled={!selectedRegion}
                      >
                        <MenuItem value="">All Districts</MenuItem>
                        {districts?.map((district) => (
                          <MenuItem key={district.district_code} value={district.district_code}>
                            {district.district_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>School Type (Optional)</InputLabel>
                      <Select
                        value={selectedSchoolType}
                        label="School Type (Optional)"
                        onChange={(e) => setSelectedSchoolType(e.target.value)}
                      >
                        <MenuItem value="">All School Types</MenuItem>
                        <MenuItem value="PUBLIC">Government</MenuItem>
                        <MenuItem value="PRIVATE">Private</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 2, mt: 2, fontWeight: 'bold' }}>
                      Export Options:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Button 
                      variant="contained" 
                      color="success"
                      startIcon={isExporting && exportingType === 'schools' ? <CircularProgress size={20} color="inherit" /> : <ExcelIcon />}
                      onClick={() => handleExportSchoolsExcel('schools')}
                      disabled={isExporting}
                      fullWidth
                      sx={{ mb: 1 }}
                    >
                      {isExporting && exportingType === 'schools' ? 'Exporting...' : 'Export Schools'}
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Button 
                      variant="contained" 
                      color="primary"
                      startIcon={isExporting && exportingType === 'enrollment' ? <CircularProgress size={20} color="inherit" /> : <ExcelIcon />}
                      onClick={() => handleExportSchoolsExcel('enrollment')}
                      disabled={isExporting}
                      fullWidth
                      sx={{ mb: 1 }}
                    >
                      {isExporting && exportingType === 'enrollment' ? 'Exporting...' : 'Enrollment Report'}
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Button 
                      variant="contained" 
                      color="secondary"
                      startIcon={isExporting && exportingType === 'infrastructure' ? <CircularProgress size={20} color="inherit" /> : <ExcelIcon />}
                      onClick={() => handleExportSchoolsExcel('infrastructure')}
                      disabled={isExporting}
                      fullWidth
                      sx={{ mb: 1 }}
                    >
                      {isExporting && exportingType === 'infrastructure' ? 'Exporting...' : 'Infrastructure Report'}
                    </Button>
                  </Grid>
                </Grid>

                {/* Download Button */}
                {exportedFile && (
                  <>
                    <Alert severity="success" sx={{ mt: 2 }}>
                      Excel file generated successfully! Click the button below to download.
                    </Alert>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                      <Button
                        variant="contained"
                        color="success"
                        size="large"
                        startIcon={<ExcelIcon />}
                        onClick={handleDownloadFile}
                        sx={{
                          px: 4,
                          py: 1.5,
                          fontSize: '1.1rem',
                          fontWeight: 'bold',
                        }}
                      >
                        Download {exportedFile.filename}
                      </Button>
                    </Box>
                  </>
                )}

                {/* Schools Data Preview */}
                {geoData?.data && (
                  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>School Name</TableCell>
                          <TableCell>Region</TableCell>
                          <TableCell>District</TableCell>
                          <TableCell align="right">Total Students</TableCell>
                          <TableCell align="right">Male</TableCell>
                          <TableCell align="right">Female</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {geoData.data.slice(0, 50).map((school: any, index: number) => (
                          <TableRow key={index} hover>
                            <TableCell>{school.school_name}</TableCell>
                            <TableCell>{school.region_name}</TableCell>
                            <TableCell>{school.district_name}</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold">
                                {formatNumber(school.total_students || 0)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{formatNumber(school.male_students || 0)}</TableCell>
                            <TableCell align="right">{formatNumber(school.female_students || 0)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                
                {geoData?.data && geoData.data.length > 50 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Showing first 50 schools. Total: {geoData.data.length} schools. Export Excel for complete data.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        {/* Enrollment Data Tab - Django Models */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  📊 Imported Enrollment Data
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  View detailed enrollment data with age and gender breakdown from SIS database sync.
                </Typography>

                {/* Sync Controls */}
                <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                  <Typography variant="h6" gutterBottom>
                    Data Synchronization
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Sync enrollment data from SIS database or trigger a full system sync.
                  </Typography>
                  
                  <Grid container spacing={2} alignItems="center">
                    <Grid item>
                      <ButtonGroup variant="outlined" disabled={isSyncing}>
                        <Button
                          startIcon={isSyncing ? <CircularProgress size={16} /> : <SyncIcon />}
                          onClick={() => handleEnrollmentSync(false)}
                        >
                          {isSyncing ? 'Syncing...' : 'Sync Enrollment'}
                        </Button>
                        <Button
                          startIcon={<RefreshIcon />}
                          onClick={() => handleEnrollmentSync(true)}
                          color="warning"
                        >
                          Clear & Sync
                        </Button>
                        <Button
                          startIcon={<SyncIcon />}
                          onClick={handleFullSync}
                          color="secondary"
                        >
                          Full SIS Sync
                        </Button>
                      </ButtonGroup>
                    </Grid>
                    
                    {syncStatus && (
                      <Grid item xs>
                        <Typography variant="caption" color="text.secondary">
                          Last sync: {syncStatus.last_sync ? 
                            new Date(syncStatus.last_sync.timestamp).toLocaleString() : 
                            'Never'}
                        </Typography>
                        <br />
                        <Typography variant="caption" color="text.secondary">
                          Config: {syncStatus.config_name}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  {/* Sync Result Alert */}
                  {syncResult && (
                    <Alert 
                      severity={syncResult.status === 'success' ? 'success' : 'error'} 
                      sx={{ mt: 2 }}
                      onClose={() => setSyncResult(null)}
                    >
                      <Typography variant="body2">
                        {syncResult.message}
                      </Typography>
                      {syncResult.sync_results && syncResult.status === 'success' && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                          Imported: {syncResult.sync_results.imported}, 
                          Updated: {syncResult.sync_results.updated}, 
                          Skipped: {syncResult.sync_results.skipped}, 
                          Errors: {syncResult.sync_results.errors}
                        </Typography>
                      )}
                    </Alert>
                  )}
                </Paper>

                {enrollmentLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : enrollmentError ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Failed to load enrollment data: {(enrollmentError as Error)?.message || 'Unknown error'}
                  </Alert>
                ) : enrollmentData ? (
                  <>
                    {/* Overview Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h4" color="primary">
                            {enrollmentData.overview.total_students.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Students
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h4" color="success.main">
                            {enrollmentData.overview.total_schools}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Schools
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h4" color="info.main">
                            {enrollmentData.overview.female_percentage}%
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Female Students
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h4" color="warning.main">
                            {enrollmentData.overview.gender_parity_index || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Gender Parity Index
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* Export Button */}
                    <Box sx={{ mb: 3 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<ExcelIcon />}
                        onClick={async () => {
                          try {
                            setIsExporting(true);
                            const blob = await exportEnrollmentExcel(selectedDistrict, parseInt(selectedAcademicYear));
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `enrollment_data_${selectedAcademicYear}_${new Date().toISOString().split('T')[0]}.xlsx`;
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                          } catch (error) {
                            console.error('Export failed:', error);
                          } finally {
                            setIsExporting(false);
                          }
                        }}
                        disabled={isExporting}
                      >
                        {isExporting ? 'Exporting...' : 'Export Enrollment Excel'}
                      </Button>
                    </Box>

                    {/* Age Distribution Table */}
                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                      Age Distribution
                    </Typography>
                    <TableContainer component={Paper} sx={{ mb: 3 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Age</TableCell>
                            <TableCell align="right">Male</TableCell>
                            <TableCell align="right">Female</TableCell>
                            <TableCell align="right">Total</TableCell>
                            <TableCell align="right">Female %</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {enrollmentData.age_distribution.map((age) => (
                            <TableRow key={age.age}>
                              <TableCell>{age.age}</TableCell>
                              <TableCell align="right">{age.male.toLocaleString()}</TableCell>
                              <TableCell align="right">{age.female.toLocaleString()}</TableCell>
                              <TableCell align="right">{age.total.toLocaleString()}</TableCell>
                              <TableCell align="right">
                                {age.total > 0 ? ((age.female / age.total) * 100).toFixed(1) : '0.0'}%
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* District Breakdown Table */}
                    <Typography variant="h6" gutterBottom>
                      District Breakdown
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>District</TableCell>
                            <TableCell>Region</TableCell>
                            <TableCell align="right">Schools</TableCell>
                            <TableCell align="right">Male</TableCell>
                            <TableCell align="right">Female</TableCell>
                            <TableCell align="right">Total</TableCell>
                            <TableCell align="right">Female %</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {enrollmentData.district_breakdown.map((district) => (
                            <TableRow key={district.district_code}>
                              <TableCell>{district.district_name}</TableCell>
                              <TableCell>{district.region_name}</TableCell>
                              <TableCell align="right">{district.total_schools}</TableCell>
                              <TableCell align="right">{district.total_male.toLocaleString()}</TableCell>
                              <TableCell align="right">{district.total_female.toLocaleString()}</TableCell>
                              <TableCell align="right">{district.total_students.toLocaleString()}</TableCell>
                              <TableCell align="right">{district.female_percentage}%</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                ) : (
                  <Alert severity="info">
                    No enrollment data available. Please import enrollment data first.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default AdvancedAnalysis;